import {
    PublicServerDirectoryResponseSchema,
    PublicServerProfileSchema,
    type PublicServerBadge,
    type PublicServerDirectoryResponse,
    type PublicServerJoinAccess,
    type PublicServerProfile,
} from "@logscord/public-server";
import {
    defineComponent,
    type PropType,
} from "vue";

export type PublicServerDirectoryItem = PublicServerProfile;
export type { PublicServerDirectoryResponse };
export type PublicServerIdentity = Pick<PublicServerProfile, "name" | "avatarUrl">;

type JoinExplanationKind = Exclude<PublicServerJoinAccess["kind"], "direct">;

const JoinPresentation: Record<JoinExplanationKind, {
    icon: string;
    titleKey: string;
    descriptionKey: string;
}> = {
    paused: {
        icon           : "link_off",
        titleKey       : "servers.modal.paused.title",
        descriptionKey : "servers.modal.paused.description",
    },
    application: {
        icon           : "approval",
        titleKey       : "servers.modal.application.title",
        descriptionKey : "servers.modal.application.description",
    },
    none: {
        icon           : "link_off",
        titleKey       : "servers.modal.unavailable.title",
        descriptionKey : "servers.modal.unavailable.description",
    },
};

export function isPublicServerDirectoryItem(value: unknown): value is PublicServerDirectoryItem {
    return PublicServerProfileSchema.safeParse(value).success;
}

export function isPublicServerDirectoryResponse(value: unknown): value is PublicServerDirectoryResponse {
    return PublicServerDirectoryResponseSchema.safeParse(value).success;
}

export function formatDirectoryCount(
    value: number | null,
    locale: string,
): string {
    if (value === null)
        return "—";

    return new Intl.NumberFormat(locale, {
        notation             : "compact",
        maximumFractionDigits: 1,
    }).format(value);
}

export function getServerInitials(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");
}

export const PublicServerAvatar = defineComponent({
    props: {
        server: {
            type     : Object as PropType<PublicServerIdentity>,
            required : true,
        },
        className: {
            type    : String,
            default : "server-directory-avatar",
        },
    },

    setup(props) {
        return () => (
            <div class={props.className}>
                {props.server.avatarUrl
                    ? <img src={props.server.avatarUrl} alt="" loading="lazy" />
                    : <span>{getServerInitials(props.server.name)}</span>}
            </div>
        );
    },
});

const BadgeIconByType = new Map<string, string>([
    [ "discord_verified", "verified_user" ],
    [ "discord_partnered", "workspace_premium" ],
    [ "ambassador", "military_tech" ],
    [ "power_user", "star" ],
    [ "early_user", "rocket_launch" ],
    [ "top_5", "looks_5" ],
    [ "top_15", "workspace_premium" ],
    [ "top_100", "leaderboard" ],
]);

export const PublicServerBadgeList = defineComponent({
    props: {
        badges: {
            type     : Array as PropType<PublicServerBadge[]>,
            required : true,
        },
        compact: {
            type    : Boolean,
            default : false,
        },
        className: {
            type    : String,
            default : "trust-badges",
        },
    },

    setup(props) {
        return () => (
            <div class={{
                [props.className]: true,
                "trust-badges-compact": props.compact,
            }}>
                {props.badges.map((badge) => (
                    <span
                        class={`trust-badge trust-badge-${badge.type}`}
                        title={badge.label}
                        aria-label={badge.label}
                    >
                        <i class="material-symbols-outlined">{BadgeIconByType.get(badge.type) ?? "stars"}</i>
                        {!props.compact && <span>{badge.label}</span>}
                    </span>
                ))}
            </div>
        );
    },
});

export const PublicServerJoinModal = defineComponent({
    props: {
        server: {
            type     : Object as PropType<PublicServerDirectoryItem | null>,
            default  : null,
        },
        translate: {
            type     : Function as PropType<(key: string) => string>,
            required : true,
        },
        locale: {
            type     : String,
            required : true,
        },
    },
    emits: [ "close" ],

    setup(props, { emit }) {
        return () => {
            const server = props.server;

            if (!server || server.join.kind === "direct")
                return null;

            const presentation = JoinPresentation[server.join.kind];

            return (
                <div class="server-directory-modal-backdrop" role="presentation" onClick={() => emit("close")}>
                    <div
                        class="server-directory-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="server-join-modal-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            class="server-directory-modal-close"
                            aria-label={props.translate("servers.modal.close")}
                            onClick={() => emit("close")}
                        >
                            <i class="material-symbols-outlined">close</i>
                        </button>

                        <i class="material-symbols-outlined server-directory-modal-icon">
                            {presentation.icon}
                        </i>
                        <h2 id="server-join-modal-title">
                            {props.translate(presentation.titleKey)}
                        </h2>
                        <p>{props.translate(presentation.descriptionKey)}</p>

                        {server.join.kind === "paused" && server.join.until && (
                            <small>
                                {props.translate("servers.modal.paused.until")}{" "}
                                {new Intl.DateTimeFormat(props.locale, {
                                    dateStyle : "medium",
                                    timeStyle : "short",
                                }).format(new Date(server.join.until))}
                            </small>
                        )}

                        {server.join.kind === "application" && (
                            <a
                                class="server-directory-modal-action"
                                href={server.join.url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {props.translate("servers.modal.application.action")}
                                <i class="material-symbols-outlined">open_in_new</i>
                            </a>
                        )}
                    </div>
                </div>
            );
        };
    },
});

export function isServerJoinDisabled(server: PublicServerDirectoryItem): boolean {
    return server.join.kind !== "direct";
}

export function openOrExplainServerJoin(
    server: PublicServerDirectoryItem,
    explain: (server: PublicServerDirectoryItem) => void,
): void {
    if (server.join.kind === "direct") {
        window.open(server.join.url, "_blank", "noopener,noreferrer");
        return;
    }

    explain(server);
}
